import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Image, Modal, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import api from './api/axiosInstance';
import { UserContext } from './UserContext';
import Sidebar from './Sidebar';
import Breadcrumb from './Breadcrumb';
import { useMobileHeaderBackAction } from './HeaderActionContext';
import useDiscoveryFetch from './components/Discovery/useDiscoveryFetch';
import MessageBubble from './components/Communities/MessageBubble';
import ConversationComposer from './components/Communities/ConversationComposer';
import ReportModal from './components/Communities/ReportModal';
import {
    generateClientMessageId,
    sortMessagesAsc,
    mergeMessages,
    removeMessageByClientId,
    getCommunityDisplayName,
    getCommunitySubtitle,
} from './utils/conversations';
import icUserGroup from './assets/img/ic-user-group.svg';

// Temporary REST-only refresh cadence until Socket.IO replaces it. Easy to
// delete: everything it touches (the effect below, POLL_INTERVAL_MS) can be
// removed as a unit once realtime messages arrive over a socket instead.
const POLL_INTERVAL_MS = 12000;

const getCommunityRefId = (conversation) => {
    if (!conversation) return null;
    if (conversation.type === 'country') return conversation.country?.iso2 || conversation.country?._id;
    if (conversation.type === 'city') return conversation.city?._id;
    return null;
};

const CommunityView = () => {
    const { id } = useParams();
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });
    const handleMobileBack = useCallback(() => navigate('/communities'), [navigate]);
    useMobileHeaderBackAction(handleMobileBack, isMobile);

    const [conversation, setConversation] = useState(null);
    const [conversationLoading, setConversationLoading] = useState(true);
    const [conversationError, setConversationError] = useState('');

    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(true);
    const [messagesError, setMessagesError] = useState('');

    const [nextCursor, setNextCursor] = useState(null);
    const [hasMoreOlder, setHasMoreOlder] = useState(false);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [loadOlderError, setLoadOlderError] = useState('');

    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState('');

    const [joiningFromDetail, setJoiningFromDetail] = useState(false);
    const [joinFromDetailError, setJoinFromDetailError] = useState('');

    const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
    const [leaving, setLeaving] = useState(false);
    const [leaveError, setLeaveError] = useState('');

    const [reportModal, setReportModal] = useState({ show: false, kind: null, target: null });
    const [reportSubmitting, setReportSubmitting] = useState(false);
    const [reportError, setReportError] = useState('');
    const [reportNotice, setReportNotice] = useState('');

    const [blockTarget, setBlockTarget] = useState(null);
    const [showBlockConfirm, setShowBlockConfirm] = useState(false);
    const [blocking, setBlocking] = useState(false);
    const [blockError, setBlockError] = useState('');

    const messagesScrollRef = useRef(null);
    const scrollActionRef = useRef(null);
    const isNearBottomRef = useRef(true);
    const messagesRef = useRef(messages);

    useEffect(() => { messagesRef.current = messages; }, [messages]);

    const markRead = useCallback(async () => {
        try {
            await api.post(`/api/conversations/${id}/read`);
        } catch (err) {
            console.error('Failed to mark conversation as read:', err);
        }
    }, [id]);

    // --- Conversation details -------------------------------------------------
    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        const load = async () => {
            try {
                if (!isReconnect) { setConversationLoading(true); setConversationError(''); }
                const res = await api.get(`/api/conversations/${id}`, { signal });
                if (!isCurrent()) return;
                setConversation(res.data);
            } catch (err) {
                if (!isCurrent()) return;
                const status = err?.response?.status;
                setConversationError(status === 404 ? 'not-found' : status === 403 ? 'forbidden' : 'generic');
            } finally {
                if (!isCurrent()) return;
                setConversationLoading(false);
            }
        };
        load();
        return undefined;
    }, [id]);

    // --- Initial messages page (only once we know the student is a member) ----
    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        if (!conversation?.isMember) {
            setMessages([]);
            setMessagesLoading(false);
            setNextCursor(null);
            setHasMoreOlder(false);
            return undefined;
        }

        const load = async () => {
            try {
                if (!isReconnect) { setMessagesLoading(true); setMessagesError(''); }
                const res = await api.get(`/api/conversations/${id}/messages`, { signal });
                if (!isCurrent()) return;
                setMessages(sortMessagesAsc(res.data.messages));
                setNextCursor(res.data.nextCursor);
                setHasMoreOlder(res.data.hasMore);
                scrollActionRef.current = { type: 'bottom' };
                markRead();
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Error fetching messages:', err);
                setMessagesError('An error occurred while loading messages.');
            } finally {
                if (!isCurrent()) return;
                setMessagesLoading(false);
            }
        };
        load();
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, conversation?.isMember]);

    // --- Temporary REST polling (remove this effect once Socket.IO lands) -----
    useEffect(() => {
        if (!conversation?.isMember) return undefined;

        let cancelled = false;
        let inFlight = false;

        const tick = async () => {
            if (cancelled || inFlight) return;
            if (document.visibilityState !== 'visible') return;
            inFlight = true;
            try {
                const res = await api.get(`/api/conversations/${id}/messages`, { params: { limit: 30 } });
                if (cancelled) return;
                const merged = mergeMessages(messagesRef.current, res.data.messages);
                const gotNew = merged.length > messagesRef.current.length;
                if (gotNew) {
                    scrollActionRef.current = isNearBottomRef.current ? { type: 'bottom' } : null;
                }
                setMessages(merged);
                if (gotNew) markRead();
            } catch (err) {
                // Background refresh failures must never replace the view with an
                // error screen — just log and try again on the next tick.
                console.error('Community message poll failed:', err);
            } finally {
                inFlight = false;
            }
        };

        const timer = setInterval(tick, POLL_INTERVAL_MS);
        const handleVisibility = () => { if (document.visibilityState === 'visible') tick(); };
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            cancelled = true;
            clearInterval(timer);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [id, conversation?.isMember, markRead]);

    // --- Scroll positioning -----------------------------------------------
    useLayoutEffect(() => {
        const action = scrollActionRef.current;
        const el = messagesScrollRef.current;
        scrollActionRef.current = null;
        if (!action || !el) return;
        if (action.type === 'bottom') {
            el.scrollTop = el.scrollHeight;
        } else if (action.type === 'preserve') {
            el.scrollTop = el.scrollHeight - action.prevScrollHeight;
        }
    }, [messages]);

    const handleScroll = () => {
        const el = messagesScrollRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        isNearBottomRef.current = distanceFromBottom < 120;
    };

    const handleLoadOlder = async () => {
        if (!nextCursor || loadingOlder) return;
        setLoadingOlder(true);
        setLoadOlderError('');
        try {
            const res = await api.get(`/api/conversations/${id}/messages`, { params: { before: nextCursor, limit: 30 } });
            const el = messagesScrollRef.current;
            scrollActionRef.current = el ? { type: 'preserve', prevScrollHeight: el.scrollHeight } : null;
            setMessages((prev) => mergeMessages(prev, res.data.messages));
            setNextCursor(res.data.nextCursor);
            setHasMoreOlder(res.data.hasMore);
        } catch (err) {
            console.error('Failed to load earlier messages:', err);
            setLoadOlderError('Failed to load earlier messages. Please try again.');
        } finally {
            setLoadingOlder(false);
        }
    };

    const refreshConversation = useCallback(async () => {
        try {
            const res = await api.get(`/api/conversations/${id}`);
            setConversation(res.data);
        } catch (err) {
            console.error('Failed to refresh conversation state:', err);
        }
    }, [id]);

    const handleSend = async (text) => {
        if (!conversation || conversation.isReadOnly) return false;
        const clientMessageId = generateClientMessageId();
        const optimistic = {
            _id: `temp-${clientMessageId}`,
            clientMessageId,
            conversation: id,
            sender: { _id: user?._id, firstName: user?.firstName, familyName: user?.familyName, profileImage: user?.profileImage },
            type: 'text',
            text,
            isDeleted: false,
            createdAt: new Date().toISOString(),
            pending: true,
        };

        scrollActionRef.current = { type: 'bottom' };
        setMessages((prev) => mergeMessages(prev, [optimistic]));
        setSending(true);
        setSendError('');

        try {
            const res = await api.post(`/api/conversations/${id}/messages`, { text, clientMessageId });
            scrollActionRef.current = { type: 'bottom' };
            setMessages((prev) => mergeMessages(prev, [res.data]));
            return true;
        } catch (err) {
            setMessages((prev) => removeMessageByClientId(prev, clientMessageId));
            const status = err?.response?.status;
            if (status === 409) {
                setSendError(err?.response?.data?.msg || 'This community just became read-only.');
                refreshConversation();
            } else if (status === 429) {
                setSendError(err?.response?.data?.msg || "You're sending messages too quickly. Please wait a moment and try again.");
            } else if (status === 403) {
                setSendError('You are no longer a member of this community.');
                refreshConversation();
            } else {
                setSendError('Failed to send message. Please try again.');
            }
            return false; // keep the typed text in the composer
        } finally {
            setSending(false);
        }
    };

    const handleJoinFromDetail = async () => {
        const type = conversation?.type;
        const refId = getCommunityRefId(conversation);
        if (!type || !refId) return;
        setJoiningFromDetail(true);
        setJoinFromDetailError('');
        try {
            const res = await api.post(`/api/conversations/communities/${type}/${refId}/join`);
            setConversation(res.data.conversation);
        } catch (err) {
            setJoinFromDetailError(err?.response?.data?.msg || 'Failed to join community. Please try again.');
        } finally {
            setJoiningFromDetail(false);
        }
    };

    const handleLeave = async () => {
        setLeaving(true);
        setLeaveError('');
        try {
            await api.post(`/api/conversations/${id}/leave`);
            navigate('/communities');
        } catch (err) {
            setLeaveError(err?.response?.data?.msg || 'Failed to leave community. Please try again.');
        } finally {
            setLeaving(false);
        }
    };

    const refetchMessagesFromScratch = useCallback(async () => {
        try {
            const res = await api.get(`/api/conversations/${id}/messages`);
            setMessages(sortMessagesAsc(res.data.messages));
            setNextCursor(res.data.nextCursor);
            setHasMoreOlder(res.data.hasMore);
            scrollActionRef.current = { type: 'bottom' };
        } catch (err) {
            console.error('Failed to refresh messages:', err);
        }
    }, [id]);

    const handleReportSubmit = async ({ reason, details }) => {
        if (!reportModal.target) return;
        setReportSubmitting(true);
        setReportError('');
        try {
            if (reportModal.kind === 'message') {
                await api.post(`/api/conversations/${id}/messages/${reportModal.target._id}/report`, { reason, details });
            } else {
                await api.post(`/api/user-blocks/${reportModal.target._id}/report`, { reason, details });
            }
            setReportModal({ show: false, kind: null, target: null });
            setReportNotice('Thanks — your report was submitted.');
        } catch (err) {
            setReportError(err?.response?.data?.msg || 'Failed to submit report. Please try again.');
        } finally {
            setReportSubmitting(false);
        }
    };

    const handleBlockConfirm = async () => {
        if (!blockTarget) return;
        setBlocking(true);
        setBlockError('');
        try {
            await api.post(`/api/user-blocks/${blockTarget._id}`);
            setShowBlockConfirm(false);
            setBlockTarget(null);
            // The backend already filters a blocked sender's messages out of the
            // read/history endpoints — re-fetch rather than filtering locally.
            await refetchMessagesFromScratch();
        } catch (err) {
            setBlockError(err?.response?.data?.msg || 'Failed to block student. Please try again.');
        } finally {
            setBlocking(false);
        }
    };

    if (conversationLoading) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <div className="py-5 text-center"><Spinner animation="border" /></div>
                    </Container>
                </main>
            </>
        );
    }

    if (conversationError || !conversation) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Breadcrumb />
                                <Alert variant={conversationError === 'not-found' ? 'warning' : 'danger'} className="mt-3">
                                    {conversationError === 'not-found'
                                        ? 'Community not found.'
                                        : 'An error occurred while loading this community.'}
                                </Alert>
                                <Button variant="outline-secondary" onClick={() => navigate('/communities')}>
                                    Back to Communities
                                </Button>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    const communityName = getCommunityDisplayName(conversation);
    const communitySubtitle = getCommunitySubtitle(conversation);
    const isMember = conversation.isMember;
    const isReadOnly = conversation.isReadOnly;

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1 community-view-page">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb label={communityName} />
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Card className="community-view-header mb-3">
                                <Card.Body>
                                    <div className="community-view-header__top">
                                        <div className="community-view-header__title-group">
                                            <Image src={icUserGroup} alt="" className="community-view-header__icon" />
                                            <div>
                                                <h1 className="community-view-header__name">{communityName}</h1>
                                                <p className="community-view-header__meta mb-0">
                                                    {[
                                                        conversation.type === 'city' ? 'City community' : 'Country community',
                                                        communitySubtitle || null,
                                                        `${conversation.memberCount || 0} member${conversation.memberCount === 1 ? '' : 's'}`,
                                                    ].filter(Boolean).join(' · ')}
                                                </p>
                                            </div>
                                        </div>

                                        {isMember && (
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="community-view-header__leave"
                                                onClick={() => setShowLeaveConfirm(true)}
                                            >
                                                Leave
                                            </Button>
                                        )}
                                    </div>

                                    {isReadOnly && (
                                        <Badge bg="secondary" className="community-view-header__readonly-badge mt-2">
                                            Read-only community
                                        </Badge>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {reportNotice && (
                        <Row>
                            <Col>
                                <Alert
                                    variant="success"
                                    dismissible
                                    className="community-view-banner"
                                    onClose={() => setReportNotice('')}
                                >
                                    {reportNotice}
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col>
                            {!isMember ? (
                                <Card className="community-view-join-card">
                                    <Card.Body className="text-center">
                                        <p>Join this community to read and send messages.</p>
                                        {joinFromDetailError && <Alert variant="danger">{joinFromDetailError}</Alert>}
                                        <Button variant="primary" onClick={handleJoinFromDetail} disabled={joiningFromDetail}>
                                            {joiningFromDetail ? 'Joining…' : 'Join community'}
                                        </Button>
                                    </Card.Body>
                                </Card>
                            ) : (
                                <Card className="community-view-chat-card">
                                    <Card.Body className="community-view-chat-card__body">
                                        {hasMoreOlder && (
                                            <div className="community-view-chat__load-older">
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={handleLoadOlder}
                                                    disabled={loadingOlder}
                                                >
                                                    {loadingOlder ? 'Loading…' : 'Load earlier messages'}
                                                </Button>
                                                {loadOlderError && <p className="text-danger mb-0 mt-2">{loadOlderError}</p>}
                                            </div>
                                        )}

                                        <div
                                            className="community-view-messages"
                                            ref={messagesScrollRef}
                                            onScroll={handleScroll}
                                        >
                                            {messagesLoading ? (
                                                <div className="py-4 text-center"><Spinner animation="border" size="sm" /></div>
                                            ) : messagesError ? (
                                                <Alert variant="danger">{messagesError}</Alert>
                                            ) : messages.length === 0 ? (
                                                <p className="community-view-messages__empty">
                                                    No messages yet. Say hello!
                                                </p>
                                            ) : (
                                                messages.map((message) => (
                                                    <MessageBubble
                                                        key={message.clientMessageId || message._id}
                                                        message={message}
                                                        isOwn={String(message.sender?._id || message.sender) === String(user?._id)}
                                                        onReportMessage={(m) => setReportModal({ show: true, kind: 'message', target: m })}
                                                        onReportStudent={(sender) => setReportModal({ show: true, kind: 'user', target: sender })}
                                                        onBlockStudent={(sender) => { setBlockTarget(sender); setShowBlockConfirm(true); }}
                                                    />
                                                ))
                                            )}
                                        </div>

                                        <ConversationComposer
                                            onSend={handleSend}
                                            sending={sending}
                                            error={sendError}
                                            disabledReason={isReadOnly
                                                ? 'This community is read-only. You can still read its history, but new messages can’t be sent.'
                                                : null}
                                        />
                                    </Card.Body>
                                </Card>
                            )}
                        </Col>
                    </Row>
                </Container>
            </main>

            {/* Leave confirmation */}
            <Modal
                className={isMobile ? 'discovery-filter-mobile-modal discovery-filter-mobile-modal--fullscreen' : undefined}
                show={showLeaveConfirm}
                onHide={() => { setShowLeaveConfirm(false); setLeaveError(''); }}
                centered={!isMobile}
                fullscreen={isMobile}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Leave community</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {leaveError && <Alert variant="danger">{leaveError}</Alert>}
                    Are you sure you want to leave {communityName}? You can rejoin from Discovery at any time.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => { setShowLeaveConfirm(false); setLeaveError(''); }}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleLeave} disabled={leaving}>
                        {leaving ? 'Leaving…' : 'Leave'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Block confirmation — blocking changes what this student sees, so it is confirmed first */}
            <Modal
                className={isMobile ? 'discovery-filter-mobile-modal discovery-filter-mobile-modal--fullscreen' : undefined}
                show={showBlockConfirm}
                onHide={() => { setShowBlockConfirm(false); setBlockError(''); }}
                centered={!isMobile}
                fullscreen={isMobile}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Block student</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {blockError && <Alert variant="danger">{blockError}</Alert>}
                    You will no longer see messages from {blockTarget?.firstName || 'this student'} in this community. Continue?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => { setShowBlockConfirm(false); setBlockError(''); }}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleBlockConfirm} disabled={blocking}>
                        {blocking ? 'Blocking…' : 'Block'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <ReportModal
                show={reportModal.show}
                onHide={() => { setReportModal({ show: false, kind: null, target: null }); setReportError(''); }}
                title={reportModal.kind === 'message' ? 'Report message' : 'Report student'}
                submitting={reportSubmitting}
                error={reportError}
                onSubmit={handleReportSubmit}
            />
        </>
    );
};

export default CommunityView;
